import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Eagerly load the 'common' namespace (used everywhere) and 'nav'
// All other namespaces are lazily imported on demand.
import commonEn from '@/locales/en/common.json';
import commonUr from '@/locales/ur/common.json';
import commonRo from '@/locales/ro/common.json';
import navEn from '@/locales/en/nav.json';
import navUr from '@/locales/ur/nav.json';
import navRo from '@/locales/ro/nav.json';
import authEn from '@/locales/en/auth.json';
import authUr from '@/locales/ur/auth.json';
import authRo from '@/locales/ro/auth.json';
import posEn from '@/locales/en/pos.json';
import posUr from '@/locales/ur/pos.json';
import posRo from '@/locales/ro/pos.json';
import productsEn from '@/locales/en/products.json';
import productsUr from '@/locales/ur/products.json';
import productsRo from '@/locales/ro/products.json';
import customersEn from '@/locales/en/customers.json';
import customersUr from '@/locales/ur/customers.json';
import customersRo from '@/locales/ro/customers.json';
import salesEn from '@/locales/en/sales.json';
import salesUr from '@/locales/ur/sales.json';
import salesRo from '@/locales/ro/sales.json';
import inventoryEn from '@/locales/en/inventory.json';
import inventoryUr from '@/locales/ur/inventory.json';
import inventoryRo from '@/locales/ro/inventory.json';
import purchasesEn from '@/locales/en/purchases.json';
import purchasesUr from '@/locales/ur/purchases.json';
import purchasesRo from '@/locales/ro/purchases.json';
import financeEn from '@/locales/en/finance.json';
import financeUr from '@/locales/ur/finance.json';
import financeRo from '@/locales/ro/finance.json';
import fbrEn from '@/locales/en/fbr.json';
import fbrUr from '@/locales/ur/fbr.json';
import fbrRo from '@/locales/ro/fbr.json';
import restaurantEn from '@/locales/en/restaurant.json';
import restaurantUr from '@/locales/ur/restaurant.json';
import restaurantRo from '@/locales/ro/restaurant.json';
import settingsEn from '@/locales/en/settings.json';
import settingsUr from '@/locales/ur/settings.json';
import settingsRo from '@/locales/ro/settings.json';
import receiptEn from '@/locales/en/receipt.json';
import receiptUr from '@/locales/ur/receipt.json';
import receiptRo from '@/locales/ro/receipt.json';
import installmentsEn from '@/locales/en/installments.json';
import installmentsUr from '@/locales/ur/installments.json';
import installmentsRo from '@/locales/ro/installments.json';
import employeesEn from '@/locales/en/employees.json';
import employeesUr from '@/locales/ur/employees.json';
import employeesRo from '@/locales/ro/employees.json';
import taxGuideEn from '@/locales/en/taxGuide.json';
import taxGuideUr from '@/locales/ur/taxGuide.json';
import taxGuideRo from '@/locales/ro/taxGuide.json';
import documentsEn from '@/locales/en/documents.json';
import documentsUr from '@/locales/ur/documents.json';
import documentsRo from '@/locales/ro/documents.json';
import communicationsEn from '@/locales/en/communications.json';
import communicationsUr from '@/locales/ur/communications.json';
import communicationsRo from '@/locales/ro/communications.json';
import platformEn from '@/locales/en/platform.json';
import recordsEn from '@/locales/en/records.json';
import platformUr from '@/locales/ur/platform.json';
import recordsUr from '@/locales/ur/records.json';
import platformRo from '@/locales/ro/platform.json';
import recordsRo from '@/locales/ro/records.json';
import catalogEn from '@/locales/en/catalog.json';
import catalogUr from '@/locales/ur/catalog.json';
import catalogRo from '@/locales/ro/catalog.json';
import mobileLinksEn from '@/locales/en/mobileLinks.json';
import mobileLinksUr from '@/locales/ur/mobileLinks.json';
import mobileLinksRo from '@/locales/ro/mobileLinks.json';

export const SUPPORTED_LANGUAGES = ['en', 'ur', 'ro'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  en: 'EN',
  ur: 'اردو',
  ro: 'Roman',
};

export const RTL_LANGUAGES: SupportedLanguage[] = ['ur'];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: [
      'common', 'nav', 'auth', 'pos', 'products', 'customers',
      'sales', 'inventory', 'purchases', 'finance', 'fbr',
      'restaurant', 'settings', 'receipt', 'installments', 'employees', 'taxGuide', 'documents',
      'communications', 'platform', 'records', 'catalog', 'mobileLinks',
    ],
    resources: {
      en: {
        common: commonEn, nav: navEn, auth: authEn, pos: posEn,
        products: productsEn, customers: customersEn, sales: salesEn,
        inventory: inventoryEn, purchases: purchasesEn, finance: financeEn,
        fbr: fbrEn, restaurant: restaurantEn, settings: settingsEn,
        receipt: receiptEn, installments: installmentsEn, employees: employeesEn, taxGuide: taxGuideEn,
        documents: documentsEn,
        communications: communicationsEn, platform: platformEn, records: recordsEn, catalog: catalogEn, mobileLinks: mobileLinksEn,
      },
      ur: {
        common: commonUr, nav: navUr, auth: authUr, pos: posUr,
        products: productsUr, customers: customersUr, sales: salesUr,
        inventory: inventoryUr, purchases: purchasesUr, finance: financeUr,
        fbr: fbrUr, restaurant: restaurantUr, settings: settingsUr,
        receipt: receiptUr, installments: installmentsUr, employees: employeesUr, taxGuide: taxGuideUr,
        documents: documentsUr,
        communications: communicationsUr, platform: platformUr, records: recordsUr, catalog: catalogUr, mobileLinks: mobileLinksUr,
      },
      ro: {
        common: commonRo, nav: navRo, auth: authRo, pos: posRo,
        products: productsRo, customers: customersRo, sales: salesRo,
        inventory: inventoryRo, purchases: purchasesRo, finance: financeRo,
        fbr: fbrRo, restaurant: restaurantRo, settings: settingsRo,
        receipt: receiptRo, installments: installmentsRo, employees: employeesRo, taxGuide: taxGuideRo,
        documents: documentsRo,
        communications: communicationsRo, platform: platformRo, records: recordsRo, catalog: catalogRo, mobileLinks: mobileLinksRo,
      },
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'pos-lang-code',
      caches: ['localStorage'],
    },
  });

export default i18n;
